import {PropsWithChildren, useEffect} from 'react';
import OktaAuth, {toRelativeUrl} from '@okta/okta-auth-js';
import {LoginCallback, Security} from '@okta/okta-react';
import {Route, Routes, useNavigate} from 'react-router-dom';

import {OktaAuthConfig} from 'core/types';

import {setOktaAuthInstance, useAuthStore} from 'core/stores/auth';
import {EnnablLogger} from 'utils/ennabl';

import {OktaAuthProviderInternal} from './OktaAuthProviderInternal';

export const OktaAuthProvider = ({children, authConfig}: PropsWithChildren<{authConfig?: OktaAuthConfig}>) => {
  const authInstance = useAuthStore(store => store.oktaAuthInstance);
  const navigate = useNavigate();

  useEffect(() => {
    if (authConfig) {
      const instance = new OktaAuth({
        issuer: authConfig.issuer,
        clientId: authConfig.clientId,
        redirectUri: window.location.origin + '/login/callback',
        scopes: ['openid', 'profile', 'email', 'offline_access'],
        tokenManager: {
          autoRenew: true,
          autoRemove: true,
          storage: 'localStorage',
        },
      });
      setOktaAuthInstance(instance);
    }
  }, [authConfig]);

  const restoreOriginalUri = (_: OktaAuth, originalUri: string) => {
    const relativeUrl = toRelativeUrl(originalUri || '/', window.location.origin);
    EnnablLogger.log(['OktaAuthProvider', 'Restoring original URI:'], {
      originalUri,
      relativeUrl,
    });

    if (relativeUrl.startsWith('/command/index.html')) {
      EnnablLogger.log(['OktaAuthProvider', 'Redirect to command index.html'], {originalUri});
      window.location.replace(originalUri);
    } else {
      navigate(relativeUrl);
    }
  };

  if (!authInstance) {
    return children;
  }

  return (
    <Security oktaAuth={authInstance} restoreOriginalUri={restoreOriginalUri}>
      <Routes>
        <Route path='/login/callback' element={<LoginCallback />} />
        <Route path='*' element={<OktaAuthProviderInternal>{children}</OktaAuthProviderInternal>} />
      </Routes>
    </Security>
  );
};
